import {useEffect, useMemo, useState} from 'react';
import {
    ApolloClient,
    from,
    InMemoryCache,
    NormalizedCacheObject,
    createHttpLink,
    ApolloLink,
} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {setContext} from '@apollo/client/link/context';
import Cookies from 'js-cookie';
import deepMerge from 'deepmerge';
import isEqual from 'lodash/isEqual';
import {
    AUTH_TOKEN_KEY,
    MAGENTO_COOKIE_STORE_CODE,
    MAGENTO_HTTP_X_MOBILE_APP_KEY,
    MAGENTO_STORE_CODE_KEY,
    X_API_KEY_HEADER_KEY,
} from '@/lib/constants';
import Router from 'next/router';
import type {AppProps} from 'next/app';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import {isMobileDevice} from '@/_application/core/hooks/useIsMobileDevice';
import {
    generateCacheId,
    getCustomerAccessToken,
} from '@/_application/modules/auth/model/auth-token';
import {CurrencyEnum} from '@/__generated__/__types__';
import {getValidStoreCode} from '@/_application/modules/stores/model/store-view.model';
import {IncomingMessage, ServerResponse} from 'node:http';
import cookie from 'cookie';
import {cacheConfig} from '@/framework/client/cache.config';
import {AuthEnum} from '@/_application/modules/auth/enum/auth';
import {createErrorLink} from '@kidslabs/module-core';
import {getInMemoryAccessToken} from '@/_application/modules/auth/model/in-memory-token-store';

interface ApolloClientOptions {
    accessToken?: string;
    storeCode?: string;
    ignoreToken?: boolean;
}

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

const isClient = typeof globalThis.window !== 'undefined';

export function createApolloClient(options?: ApolloClientOptions) {
    const authLink = setContext(async (_, {headers}) => {
        const ignoreToken = options?.ignoreToken;
        const accessToken = options?.accessToken;
        let token;
        if (!ignoreToken) {
            // Prefer in-memory token on client, fall back to async getter
            token = accessToken ?? (isClient ? getInMemoryAccessToken() : null) ?? (await getCustomerAccessToken());
        }
        const authHeader = token ? {authorization: `Bearer ${token}`} : {};

        const storeCode = options?.storeCode;
        const magentoStoreCode = storeCode ?? Cookies.get(MAGENTO_COOKIE_STORE_CODE);
        const validStoreCode = getValidStoreCode(magentoStoreCode ?? '');
        const cacheId = await generateCacheId(validStoreCode, CurrencyEnum.Vnd, !!token, token ? '1' : '0', 10, 'salt');
        const requestHeaders = {
            ...headers,
            ...authHeader,
            'app-cache-id': cacheId,
            [MAGENTO_STORE_CODE_KEY]: validStoreCode,
            [MAGENTO_COOKIE_STORE_CODE]: validStoreCode,
        };

        if (isMobileDevice()) {
            requestHeaders[MAGENTO_HTTP_X_MOBILE_APP_KEY] = 'mobile';
        }

        requestHeaders[X_API_KEY_HEADER_KEY] = isClient ? process.env.NEXT_PUBLIC_API_KEY : process.env.API_KEY_SERVER;

        return {
            headers: requestHeaders,
        };
    });

    const apiUrl = isClient
        ? process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT
        : process.env.GATEWAY_API_ENDPOINT_SERVER;

    const httpLink = createHttpLink({
        uri: apiUrl,
        credentials: 'same-origin',
    });

    return new ApolloClient({
        ssrMode: typeof globalThis.window === 'undefined',
        link: from([authLink, createErrorLink(), httpLink]),
        cache: new InMemoryCache(cacheConfig),
    });
}

export function initializeApolloServerSide(options?: {
    request?: IncomingMessage & {
        cookies: Partial<{
            [key: string]: string;
        }>;
    };
    response?: ServerResponse;
}) {
    // Handle getStaticProps case (no request/response)
    if (!options || !options.request) {
        return createApolloClient();
    }
    const cookies = cookie.parse(options.request.headers?.cookie ?? '');
    const storeCode = cookies[MAGENTO_COOKIE_STORE_CODE];
    return createApolloClient({storeCode});
}

export function initializeApolloClientSide(initialState?: NormalizedCacheObject) {
    // Create the Apollo Client once in the client
    const _apolloClient = apolloClient ?? createApolloClient();

    // If your page has Next.js data fetching methods that use Apollo Client, the initial state
    // get hydrated here
    if (initialState) {
        // Get existing cache, loaded during client side data fetching
        const existingCache = _apolloClient.extract();

        // Merge the existing cache into data passed from getStaticProps/getServerSideProps
        const data = deepMerge(initialState, existingCache, {
            // combine arrays using object equality (like in sets)
            arrayMerge: (destinationArray, sourceArray) => [
                ...sourceArray,
                ...destinationArray.filter(d => sourceArray.every(s => !isEqual(d, s))),
            ],
        });
        // Restore the cache with the merged data
        _apolloClient.cache.restore(data);
    }

    return _apolloClient;
}

export function addApolloState(client: ApolloClient<NormalizedCacheObject>, pageProps: AppProps['pageProps']) {
    if (pageProps?.props) {
        pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
    }
    return pageProps;
}

export function useApolloV1(pageProps: AppProps['pageProps']) {
    const state = pageProps[APOLLO_STATE_PROP_NAME];
    return useMemo(() => initializeApolloClientSide(state), [state]);
}

export function useApollo(pageProps: AppProps['pageProps']) {
    const state = pageProps[APOLLO_STATE_PROP_NAME];
    const [apolloClient, setApolloClient] = useState(() => initializeApolloClientSide(state));

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === AuthEnum.ACCESS_TOKEN) {
                setApolloClient(initializeApolloClientSide(state));
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [state]);

    return apolloClient;
}
/**
 * Create a new Apollo Client instance with a token
 * @param {string} token
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export async function createProductReviewUploadApolloClient() {
    const authLink = setContext(async (_, {headers}) => {
        const token = await getCustomerAccessToken();
        const authHeader = token ? {authorization: `Bearer ${token}`} : {};
        const magentoStoreCode = Cookies.get(MAGENTO_COOKIE_STORE_CODE);
        const validStoreCode = getValidStoreCode(magentoStoreCode ?? '');
        const cacheId = await generateCacheId(validStoreCode, CurrencyEnum.Vnd, !!token, token ? '1' : '0', 10, 'salt');
        const requestHeaders = {
            ...headers,
            ...authHeader,
            'app-cache-id': cacheId,
            'apollo-require-preflight': true,
            [MAGENTO_STORE_CODE_KEY]: validStoreCode,
            [MAGENTO_COOKIE_STORE_CODE]: validStoreCode,
            [X_API_KEY_HEADER_KEY]: process.env.NEXT_PUBLIC_API_KEY ?? '',
        };
        if (isMobileDevice()) {
            requestHeaders[MAGENTO_HTTP_X_MOBILE_APP_KEY] = 'mobile';
        }
        return {
            headers: requestHeaders,
        };
    });

    const httpLink = createUploadLink({
        uri: process.env.NEXT_PUBLIC_GRAPHQL_PRODUCT_REVIEW_UPLOAD_API_ENDPOINT,
        credentials: 'same-origin',
    });

    const errorLink = onError(({graphQLErrors, networkError}) => {
        if (graphQLErrors)
            graphQLErrors.map(({message, locations, path}) => {
                if (message === 'KIDSLABS_ERROR.NOT_AUTHORIZED') {
                    // every 401/unauthorized error will be caught here and update the global local state
                    Cookies.remove(AUTH_TOKEN_KEY);
                    Router.reload();
                    // authorize(false);
                } else if (/The current customer isn['’]t authorized\./.test(message)) {
                    console.log('something went wrongsss');
                }
                // console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
            });

        if (networkError) console.log(`[Network error]: ${networkError}`);
    });

    return new ApolloClient({
        ssrMode: globalThis.window === undefined,

        // @ts-expect-error apollo-upload-client type duplication
        link: ApolloLink.from([authLink, errorLink, httpLink]),
        cache: new InMemoryCache(cacheConfig),
    });
}

export const getGraphqlApiEndpointServerCloudEndpoint = () => {
    return process.env.GATEWAY_API_ENDPOINT_SERVER_CLOUD;
};

export const getMagentoGraphqlApiEndpointPublic = () => {
    return process.env.NEXT_PUBLIC_MAGENTO_GATEWAY_API_ENDPOINT;
};
